/**
 * Canonical bridge Risk Engine
 * Preserves legacy contract while incorporating weighted answer pressure.
 *
 * Hardened for mixed question payloads. The assessment flow has recently been
 * observed to pass non-array question shapes at completion time. This bridge
 * now normalizes those shapes before forwarding them to severityModel.
 */

import computeSeverityFromAnswers from "./severityModel";
import { runUniversalRiskEngine } from "./universalRiskEngine";

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function countByAnswer(answers = {}) {
  const values = Object.values(answers || {});
  let yes = 0;
  let no = 0;
  let unsure = 0;

  values.forEach((value) => {
    const normalized = String(value ?? "").toLowerCase().trim();
    if (value === true || normalized === "yes") yes += 1;
    else if (value === false || normalized === "no") no += 1;
    else if (normalized === "unsure") unsure += 1;
  });

  return { yes, no, unsure, total: values.length };
}

function normalizeQuestions(questionsInput) {
  if (Array.isArray(questionsInput)) return questionsInput;

  if (!questionsInput || typeof questionsInput !== "object") return [];

  if (Array.isArray(questionsInput.questions)) return questionsInput.questions;
  if (Array.isArray(questionsInput.items)) return questionsInput.items;
  if (Array.isArray(questionsInput.rows)) return questionsInput.rows;
  if (Array.isArray(questionsInput.data)) return questionsInput.data;

  const objectValues = Object.values(questionsInput);
  if (objectValues.every((value) => value && typeof value === "object" && !Array.isArray(value))) {
    return objectValues;
  }

  return [];
}

function computeSeveritySafely(questionsInput, answers) {
  const normalizedQuestions = normalizeQuestions(questionsInput);

  try {
    return computeSeverityFromAnswers(normalizedQuestions, answers);
  } catch (error) {
    console.error("[RiskEngine] computeSeverityFromAnswers failed", {
      message: error?.message || String(error || "Unknown severity error"),
      question_shape: Array.isArray(questionsInput)
        ? "array"
        : questionsInput && typeof questionsInput === "object"
          ? "object"
          : typeof questionsInput,
      normalized_question_count: normalizedQuestions.length,
    });

    return {
      risk_score: 0,
      risk_grade: "A",
      severity_score_0_100: 0,
      failed_question_ids: [],
      unsure_question_ids: [],
    };
  }
}

export function calculateRobustRisk({
  annualRevenue = 0,
  peopleImpacted = 0,
  jurisdictions = [],
  answers = {},
  questions = [],
  impactMultiplier = 1,
  finePercentCap = 0.07,
} = {}) {
  const safeRevenue = Number(annualRevenue) || 0;
  const safePeopleImpacted = Number(peopleImpacted) || 0;
  const safeImpactMultiplier = clamp(Number(impactMultiplier) || 1, 0.5, 3);
  const safeFinePercentCap = clamp(Number(finePercentCap) || 0.07, 0.01, 0.15);

  const severity = computeSeveritySafely(questions, answers);
  const { yes, no, unsure, total } = countByAnswer(answers);

  const answerPressure = total > 0 ? (no + unsure) / total : 0;

  const peoplePressure =
    safePeopleImpacted <= 0
      ? 0
      : clamp(Math.log10(safePeopleImpacted + 1) / 6, 0, 1);

  const jurisdictionCount = Array.isArray(jurisdictions) ? jurisdictions.length : 0;
  const jurisdictionPressure =
    jurisdictionCount <= 1 ? 0 : clamp((jurisdictionCount - 1) / 5, 0, 1);

  const severityPressure = clamp((severity?.risk_score || 0) / 100, 0, 1);

  const blendedPressure = clamp(
    severityPressure * 0.5 +
      answerPressure * 0.2 +
      peoplePressure * 0.2 +
      jurisdictionPressure * 0.1,
    0,
    1,
  );

  const grossExposureCeiling = Math.round(
    safeRevenue * safeFinePercentCap * safeImpactMultiplier * (0.35 + blendedPressure),
  );

  return {
    score: severity?.risk_score || 0,
    grade: severity?.risk_grade || "A",
    severity_score_0_100: severity?.severity_score_0_100 || 0,
    failed_question_ids: severity?.failed_question_ids || [],
    unsure_question_ids: severity?.unsure_question_ids || [],
    inputs: {
      annualRevenue: safeRevenue,
      peopleImpacted: safePeopleImpacted,
      jurisdictionCount,
      finePercentCap: safeFinePercentCap,
      impactMultiplier: safeImpactMultiplier,
      yes,
      no,
      unsure,
      total,
    },
    summary: {
      gross_exposure_ceiling_usd: grossExposureCeiling,
      computed_gross_exposure_ceiling: grossExposureCeiling,
      blended_pressure: blendedPressure,
      answer_pressure: answerPressure,
      people_pressure: peoplePressure,
      jurisdiction_pressure: jurisdictionPressure,
    },
    fine_selection: {
      cap_percent_used: safeFinePercentCap,
      impact_multiplier_used: safeImpactMultiplier,
    },
  };
}

export { runUniversalRiskEngine };

export default calculateRobustRisk;
